import React, { useRef, useEffect } from 'react';
import _get from 'lodash/get';
import brandColors from 'styles/brandColors';
import makeStyles from '@material-ui/core/styles/makeStyles';
import classNames from 'classnames';
import TruncateItemWithTooltip from 'helpers/truncateItemWithTooltip';
import comparisonToDisplayName from 'pages/RolesAndPermissions/helpers/comparisonToDisplayName';

import {
  CONTENT_TYPE_FIELD,
  CONTENT_TYPE_FIELD_FOR_SAME_ATTR,
  CONTENT_TYPE_COMPARISON,
  CONTENT_TYPE_VALUE,
  COMPARISON_TYPE_RELATIVE,
  SAME_ATTR_RELATIVE_FILTER,
  RELATIVE_FILTERS_NAME_LOOKUP,
  BACKDROP_Z_INDEX,
} from 'pages/RolesAndPermissions/consts';

import IconButton from '@material-ui/core/IconButton';
import Button from '@material-ui/core/Button';
import Box from '@material-ui/core/Box';
import CancelRoundedIcon from '@material-ui/icons/CancelRounded';
import * as Types from 'pages/RolesAndPermissions/typings';

interface Props {
  filter: Types.Filter | Types.RelationalFilter;
  dropdownContentType: Types.DropdownContentType;
  isFirstInGroup?: boolean;
  filterIdInFocus?: string;
  disabled: boolean;
  onSetFilterIdInFocus: (filterId: string) => void;
  onDelete: () => void;
  onOpenDropdown: (e: React.MouseEvent<HTMLElement>) => void;
  onSetDropdownAnchorEl: (el: HTMLElement | null) => void;
  onSetDropdownContentType: (type: Types.DropdownContentType) => void;
}

function Filter({
  filter,
  dropdownContentType,
  isFirstInGroup,
  filterIdInFocus,
  disabled,
  onSetFilterIdInFocus,
  onDelete,
  onOpenDropdown,
  onSetDropdownAnchorEl,
  onSetDropdownContentType,
}: Props) {
  const classes = useStyles();
  const field = filter.field;
  const comparison = filter.comparison;
  const value = 'value' in filter ? filter.value : undefined;
  const type = 'type' in filter ? filter.type : undefined;
  const fieldButtonRef = useRef<HTMLButtonElement>(null);
  const fieldForSameAttrButtonRef = useRef<HTMLButtonElement>(null);
  const comparisonButtonRef = useRef<HTMLButtonElement>(null);
  const valueButtonRef = useRef<HTMLButtonElement>(null);
  const shouldShowValueInput = !['hav', 'ukn'].includes(comparison);

  const fieldInput =
    comparison === COMPARISON_TYPE_RELATIVE
      ? RELATIVE_FILTERS_NAME_LOOKUP[type as keyof typeof RELATIVE_FILTERS_NAME_LOOKUP]
      : _get(field, 'displayName');

  useEffect(() => {
    if (filterIdInFocus === filter.id) {
      if (dropdownContentType === CONTENT_TYPE_FIELD) {
        onSetDropdownAnchorEl(fieldButtonRef.current);
      } else if (dropdownContentType === CONTENT_TYPE_FIELD_FOR_SAME_ATTR) {
        onSetDropdownAnchorEl(fieldForSameAttrButtonRef.current);
      } else if (dropdownContentType === CONTENT_TYPE_COMPARISON) {
        onSetDropdownAnchorEl(comparisonButtonRef.current);
      } else if (dropdownContentType === CONTENT_TYPE_VALUE) {
        onSetDropdownAnchorEl(valueButtonRef.current);
      } else {
        onSetDropdownAnchorEl(null);
      }
    }
  }, [filterIdInFocus, filter.id, dropdownContentType, onSetDropdownAnchorEl]);

  const handleOpenDropdown = (
    e: React.MouseEvent<HTMLElement>,
    type: Types.DropdownContentType,
  ) => {
    onSetFilterIdInFocus(filter.id);
    onOpenDropdown(e);
    onSetDropdownContentType(type);
  };

  return (
    <Box
      display="inline-flex"
      alignItems="center"
      className={classNames(classes.root, {
        [classes.isFirstInGroup]: isFirstInGroup,
      })}
    >
      <IconButton
        onClick={onDelete}
        size="small"
        disabled={disabled}
        // (note - pearl) This class makes sure that when dropdown is opened, the delete button
        // of the filter in focus is clickable while other delete buttons are not
        className={classNames({ [classes.clickableDelete]: filterIdInFocus === filter.id })}
      >
        <CancelRoundedIcon
          className={classNames(classes.deleteIcon, disabled && classes.disabled)}
        />
      </IconButton>
      <Button
        className={classes.button}
        onClick={e => handleOpenDropdown(e, CONTENT_TYPE_FIELD)}
        ref={el => {
          // @ts-ignore
          fieldButtonRef.current = el;
        }}
        disabled={disabled}
      >
        <TruncateItemWithTooltip option={fieldInput || 'Select a filter'} />
      </Button>
      {fieldInput && type === SAME_ATTR_RELATIVE_FILTER.type && (
        <Button
          className={classes.button}
          onClick={e => handleOpenDropdown(e, CONTENT_TYPE_FIELD_FOR_SAME_ATTR)}
          ref={el => {
            // @ts-ignore
            fieldForSameAttrButtonRef.current = el;
          }}
          disabled={disabled}
        >
          <TruncateItemWithTooltip option={field ? field.displayName : 'Select a field'} />
        </Button>
      )}
      {field && comparison !== COMPARISON_TYPE_RELATIVE && (
        <Button
          className={classes.button}
          onClick={e => handleOpenDropdown(e, CONTENT_TYPE_COMPARISON)}
          ref={el => {
            // @ts-ignore
            comparisonButtonRef.current = el;
          }}
          disabled={disabled}
        >
          {comparisonToDisplayName(comparison, field.type || '') || 'Select comparison'}
        </Button>
      )}
      {field && comparison && comparison !== COMPARISON_TYPE_RELATIVE && shouldShowValueInput && (
        <Button
          className={classes.button}
          onClick={e => handleOpenDropdown(e, CONTENT_TYPE_VALUE)}
          ref={el => {
            // @ts-ignore
            valueButtonRef.current = el;
          }}
          disabled={disabled}
        >
          <TruncateItemWithTooltip option={value || 'Add a value'} />
        </Button>
      )}
    </Box>
  );
}

const useStyles = makeStyles(theme => ({
  root: {
    whiteSpace: 'nowrap',
    borderColor: 'rgba(0, 0, 0, 0.1)',
    borderStyle: 'solid',
    borderWidth: 1,
  },
  deleteIcon: { color: theme.palette.common.black },
  button: {
    paddingLeft: theme.spacing(0.5),
    paddingRight: theme.spacing(0.5),
    fontWeight: theme.typography.fontWeightRegular,
    minWidth: 0,
    maxWidth: 200,
  },
  isFirstInGroup: {
    borderTopLeftRadius: 4,
    borderBottomLeftRadius: 4,
  },
  disabled: {
    color: brandColors.grayScale.gray1,
  },
  clickableDelete: {
    zIndex: BACKDROP_Z_INDEX + 1,
    backgroundColor: theme.palette.common.white,
  },
}));

export default Filter;
